/**
 * Curve diagrams – a drawn, labelled set of axes with curves on it, the kind a
 * teacher sketches on a board: two lines crossing, the dashed guides down to
 * each axis, a shaded area between them, an arrow showing one curve moving.
 *
 * This is deliberately NOT a chart. A chart plots data; a curve diagram shows
 * a relationship, and what matters is where the curves cross, not what number
 * that is. So the spec names things rather than placing them: a point is "the
 * intersection of these two curves", a region's corner is "that point's
 * height, on the axis", and the renderer works out the geometry. Nothing the
 * author writes can put an equilibrium label somewhere the curves don't meet.
 *
 * It is also deliberately not tied to a subject. Supply and demand is the
 * obvious customer, but a velocity-time sketch, an enzyme activity curve or a
 * population growth curve is the same picture with different words on it.
 * Subject knowledge lives in curve-diagram-presets.js, which only ever
 * produces one of these specs.
 *
 *   {
 *     title,                                    // accessible name, not drawn
 *     axes: { origin, x: { label, min, max, numeric, marks: [{ at, label }] }, y: {…} },
 *     curves:  [{ id, label, points: [{x,y}…], smooth, style, tone, step }],
 *     points:  [{ id, label, at | intersect | on, guides, xLabel, yLabel, dot, step }],
 *     regions: [{ label, vertices: [ref…], tone, step }],
 *     arrows:  [{ from, to, label, heads, along, step }],
 *     notes:   [{ text, at, step }],
 *   }
 *
 * A `ref` is a point id, or `{ x | xOf, y | yOf }` where xOf/yOf borrow a
 * coordinate from a named point – `{ x: 0, yOf: 'E1' }` is "E1's height, on
 * the y axis".
 *
 * `step` (1 by default) orders a build: render with `{ step: 2 }` and only
 * what belongs to steps 1 and 2 is drawn. `{ blank: true }` draws the axes and
 * their titles alone, for a diagram the student is asked to draw.
 *
 * Loaded in the browser as window.CurveDiagram and require()'d in Node (UMD,
 * no DOM – pure string building, so the printed sheet, the PDF and the slide
 * all get the same picture). Styling is inline for the same reason: the SVG
 * has to survive being lifted out of the page and rasterised.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.CurveDiagram = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const LIMITS = { curves: 8, curvePoints: 60, points: 12, regions: 6, regionVertices: 40, arrows: 6, notes: 6, marks: 8, label: 60, steps: 6 };
    const TONES = ['a', 'b', 'c', 'd', 'ink', 'muted'];
    const GUIDES = ['both', 'x', 'y', 'none'];

    const DEFAULT_THEME = {
        ink: '#1f2937',
        muted: '#6b7280',
        guide: '#6b7280',
        grid: '#e3e6ee',
        paper: '#ffffff',
        tones: { a: '#2448d8', b: '#c2410c', c: '#0f766e', d: '#7e22ce', ink: '#1f2937', muted: '#6b7280' },
        font: 'Arial, Helvetica, sans-serif',
    };

    function esc(str) {
        return String(str == null ? '' : str)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const str = (v, max = LIMITS.label) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
    const idOf = (v) => (typeof v === 'string' && /^[\w*'+-]{1,24}$/.test(v.trim()) ? v.trim() : '');
    const oneOf = (v, allowed, fallback) => (allowed.includes(v) ? v : fallback);
    const stepOf = (v) => (Number.isInteger(v) && v >= 1 ? Math.min(v, LIMITS.steps) : 1);
    const r2 = (n) => Math.round(n * 100) / 100;

    // ---- Normalising -------------------------------------------------------

    function normAxis(raw, fallbackLabel) {
        const a = raw && typeof raw === 'object' ? raw : {};
        let min = num(a.min), max = num(a.max);
        if (min === null || max === null || max <= min) { min = 0; max = 100; }
        const marks = (Array.isArray(a.marks) ? a.marks : [])
            .map(m => ({ at: num(m && m.at), label: str(m && m.label) }))
            .filter(m => m.at !== null && m.label && m.at >= min && m.at <= max)
            .slice(0, LIMITS.marks);
        return { label: str(a.label) || fallbackLabel, min, max, numeric: a.numeric === true, marks };
    }

    /** `{ x | xOf, y | yOf }`, or a bare point id. Null when it names nothing usable. */
    function normRef(raw) {
        if (typeof raw === 'string') return idOf(raw) || null;
        if (!raw || typeof raw !== 'object') return null;
        const out = {};
        if (num(raw.x) !== null) out.x = raw.x; else if (idOf(raw.xOf)) out.xOf = idOf(raw.xOf); else return null;
        if (num(raw.y) !== null) out.y = raw.y; else if (idOf(raw.yOf)) out.yOf = idOf(raw.yOf); else return null;
        return out;
    }

    function normPointSource(p) {
        if (Array.isArray(p.intersect) && p.intersect.length === 2 && idOf(p.intersect[0]) && idOf(p.intersect[1])) {
            return { intersect: [idOf(p.intersect[0]), idOf(p.intersect[1])], pick: oneOf(p.pick, ['first', 'last'], 'first') };
        }
        if (p.on && typeof p.on === 'object' && idOf(p.on.curve)) {
            const on = { curve: idOf(p.on.curve) };
            if (num(p.on.x) !== null) on.x = p.on.x;
            else if (idOf(p.on.xOf)) on.xOf = idOf(p.on.xOf);
            else if (num(p.on.y) !== null) on.y = p.on.y;
            else if (idOf(p.on.yOf)) on.yOf = idOf(p.on.yOf);
            else return null;
            return { on, pick: oneOf(p.pick, ['first', 'last'], 'first') };
        }
        const at = normRef(p.at);
        return at && typeof at === 'object' ? { at } : null;
    }

    /**
     * Coerce anything – a model's tool output, a stored row, a preset's build –
     * into a spec the renderer can trust, or null when there is nothing to draw.
     *
     * Drops what it cannot use rather than failing the whole diagram: a region
     * with a bad corner goes, the curves it sat between stay. References are
     * checked here too, so render() never has to wonder whether an id exists.
     */
    function normalize(raw) {
        if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
        if (!raw || typeof raw !== 'object') return null;
        const axesRaw = raw.axes && typeof raw.axes === 'object' ? raw.axes : {};
        const axes = { origin: str(axesRaw.origin, 4), x: normAxis(axesRaw.x, ''), y: normAxis(axesRaw.y, '') };

        const seen = new Set();
        const claim = (id) => { if (!id || seen.has(id)) return ''; seen.add(id); return id; };

        const curves = (Array.isArray(raw.curves) ? raw.curves : []).map((c, i) => {
            if (!c || typeof c !== 'object') return null;
            const points = (Array.isArray(c.points) ? c.points : [])
                .map(p => ({ x: num(p && p.x), y: num(p && p.y) }))
                .filter(p => p.x !== null && p.y !== null)
                .slice(0, LIMITS.curvePoints);
            if (points.length < 2) return null;
            const id = claim(idOf(c.id) || `c${i + 1}`);
            if (!id) return null;
            return {
                id, label: str(c.label), points,
                smooth: c.smooth === true,
                style: oneOf(c.style, ['solid', 'dashed'], 'solid'),
                tone: oneOf(c.tone, TONES, TONES[i % 4]),
                step: stepOf(c.step),
            };
        }).filter(Boolean).slice(0, LIMITS.curves);
        const curveIds = new Set(curves.map(c => c.id));

        const pointIds = new Set();
        const refOk = (ref) => {
            if (typeof ref === 'string') return pointIds.has(ref);
            return (ref.xOf === undefined || pointIds.has(ref.xOf)) && (ref.yOf === undefined || pointIds.has(ref.yOf));
        };
        // In order, because a point may borrow a coordinate from an earlier one.
        const points = [];
        for (const p of (Array.isArray(raw.points) ? raw.points : [])) {
            if (!p || typeof p !== 'object' || points.length >= LIMITS.points) continue;
            const source = normPointSource(p);
            if (!source) continue;
            if (source.intersect && !source.intersect.every(id => curveIds.has(id))) continue;
            if (source.on && (!curveIds.has(source.on.curve)
                || (source.on.xOf && !pointIds.has(source.on.xOf)) || (source.on.yOf && !pointIds.has(source.on.yOf)))) continue;
            if (source.at && !refOk(source.at)) continue;
            const id = claim(idOf(p.id) || `p${points.length + 1}`);
            if (!id) continue;
            pointIds.add(id);
            points.push({
                id, label: str(p.label), ...source,
                guides: oneOf(p.guides, GUIDES, 'none'),
                xLabel: str(p.xLabel), yLabel: str(p.yLabel),
                dot: p.dot !== false,
                step: stepOf(p.step),
            });
        }

        const regions = (Array.isArray(raw.regions) ? raw.regions : []).map((r, i) => {
            if (!r || typeof r !== 'object') return null;
            const vertices = (Array.isArray(r.vertices) ? r.vertices : []).map(normRef).slice(0, LIMITS.regionVertices);
            if (vertices.length < 3 || vertices.some(v => !v || !refOk(v))) return null;
            return { label: str(r.label), vertices, tone: oneOf(r.tone, TONES, TONES[(i + 2) % 4]), step: stepOf(r.step) };
        }).filter(Boolean).slice(0, LIMITS.regions);

        // An arrow end is a point, a place, or a curve - curve to curve is "this
        // one moved to there", which is what most arrows on these diagrams say.
        const endOk = (e) => (typeof e === 'string' ? (curveIds.has(e) || pointIds.has(e)) : !!e && refOk(e));
        const normEnd = (e) => (typeof e === 'string' ? idOf(e) : normRef(e));
        const arrows = (Array.isArray(raw.arrows) ? raw.arrows : []).map((a) => {
            if (!a || typeof a !== 'object') return null;
            const from = normEnd(a.from), to = normEnd(a.to);
            if (!from || !to || !endOk(from) || !endOk(to)) return null;
            return { from, to, label: str(a.label), heads: oneOf(a.heads, ['end', 'both'], 'end'), along: oneOf(a.along, ['x', 'y'], 'x'), step: stepOf(a.step) };
        }).filter(Boolean).slice(0, LIMITS.arrows);

        const notes = (Array.isArray(raw.notes) ? raw.notes : []).map((n) => {
            if (!n || typeof n !== 'object') return null;
            const at = normRef(n.at), text = str(n.text);
            if (!text || !at || !refOk(at)) return null;
            return { text, at, step: stepOf(n.step) };
        }).filter(Boolean).slice(0, LIMITS.notes);

        if (!curves.length && !points.length) return null;
        const spec = { title: str(raw.title, 120), axes, curves, points, regions, arrows, notes };
        if (raw.toDraw === true) spec.toDraw = true;
        // Where the spec came from, when a preset built it: enough for an
        // editor to show the same choices again. Never read by the renderer.
        const src = raw.source;
        if (src && typeof src === 'object' && idOf(src.preset)) {
            const options = {};
            for (const [k, v] of Object.entries(src.options && typeof src.options === 'object' ? src.options : {}).slice(0, 8)) {
                if (idOf(k) && idOf(v)) options[k] = v;
            }
            spec.source = { preset: idOf(src.preset), options };
        }
        return spec;
    }

    // ---- Geometry ----------------------------------------------------------

    /** Catmull-Rom through the given points, as a dense polyline. */
    function sample(curve) {
        const p = curve.points;
        if (!curve.smooth || p.length < 3) return p.map(q => ({ x: q.x, y: q.y }));
        const out = [];
        const STEPS = 14;
        for (let i = 0; i < p.length - 1; i++) {
            const p0 = p[Math.max(0, i - 1)], p1 = p[i], p2 = p[i + 1], p3 = p[Math.min(p.length - 1, i + 2)];
            for (let s = 0; s < STEPS; s++) {
                const t = s / STEPS, t2 = t * t, t3 = t2 * t;
                out.push({
                    x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
                    y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
                });
            }
        }
        out.push({ x: p[p.length - 1].x, y: p[p.length - 1].y });
        return out;
    }

    function segmentHit(a, b, c, d) {
        const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
        const denom = rx * sy - ry * sx;
        if (Math.abs(denom) < 1e-12) return null;
        const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
        const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denom;
        if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
        return { x: a.x + t * rx, y: a.y + t * ry };
    }

    /** Every place two polylines cross, left to right, without near-duplicates. */
    function intersections(a, b) {
        const hits = [];
        for (let i = 0; i < a.length - 1; i++) {
            for (let j = 0; j < b.length - 1; j++) {
                const h = segmentHit(a[i], a[i + 1], b[j], b[j + 1]);
                if (h && !hits.some(o => Math.abs(o.x - h.x) < 1e-6 && Math.abs(o.y - h.y) < 1e-6)) hits.push(h);
            }
        }
        return hits.sort((p, q) => p.x - q.x || p.y - q.y);
    }

    /** Where a polyline passes through a given x (or y), left to right. */
    function crossings(pts, axis, value) {
        const other = axis === 'x' ? 'y' : 'x';
        const hits = [];
        for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i], b = pts[i + 1];
            const lo = Math.min(a[axis], b[axis]), hi = Math.max(a[axis], b[axis]);
            if (value < lo - 1e-9 || value > hi + 1e-9) continue;
            const span = b[axis] - a[axis];
            const t = Math.abs(span) < 1e-12 ? 0 : (value - a[axis]) / span;
            const h = { [axis]: value, [other]: a[other] + t * (b[other] - a[other]) };
            if (!hits.some(o => Math.abs(o.x - h.x) < 1e-6 && Math.abs(o.y - h.y) < 1e-6)) hits.push(h);
        }
        return hits.sort((p, q) => p.x - q.x || p.y - q.y);
    }

    /**
     * Resolve a normalised spec into coordinates: each curve as a polyline and
     * each point as an {x, y} in axis units. A point that names an intersection
     * the curves don't have simply isn't in the result, and anything hanging
     * off it is skipped at draw time.
     */
    function solve(spec) {
        const curves = {};
        for (const c of spec.curves) curves[c.id] = sample(c);
        const points = {};
        const pickOf = (hits, pick) => (hits.length ? (pick === 'last' ? hits[hits.length - 1] : hits[0]) : null);
        const coord = (ref) => {
            if (typeof ref === 'string') return points[ref] || null;
            const x = ref.x !== undefined ? ref.x : (points[ref.xOf] ? points[ref.xOf].x : null);
            const y = ref.y !== undefined ? ref.y : (points[ref.yOf] ? points[ref.yOf].y : null);
            return x === null || y === null ? null : { x, y };
        };
        for (const p of spec.points) {
            let at = null;
            if (p.intersect) at = pickOf(intersections(curves[p.intersect[0]], curves[p.intersect[1]]), p.pick);
            else if (p.on) {
                const pts = curves[p.on.curve];
                if (p.on.x !== undefined || p.on.xOf) {
                    const x = p.on.x !== undefined ? p.on.x : (points[p.on.xOf] ? points[p.on.xOf].x : null);
                    if (x !== null) at = pickOf(crossings(pts, 'x', x), p.pick);
                } else {
                    const y = p.on.y !== undefined ? p.on.y : (points[p.on.yOf] ? points[p.on.yOf].y : null);
                    if (y !== null) at = pickOf(crossings(pts, 'y', y), p.pick);
                }
            } else at = coord(p.at);
            if (at) points[p.id] = { x: at.x, y: at.y };
        }
        return { curves, points, coord };
    }

    /** The references a spec makes that its geometry cannot satisfy – for tests and for callers that want to refuse a broken diagram. */
    function unresolved(spec) {
        const { points } = solve(spec);
        return spec.points.filter(p => !points[p.id]).map(p => p.id);
    }

    // ---- Drawing -----------------------------------------------------------

    /**
     * Label text with subscripts. `P_1`, `Q_e` and `S_{tax}` are explicit; a
     * short capitalised name ending in a digit (P1, AD2, SRAS1) is subscripted
     * on sight, because that is how every one of them is written by hand.
     * Shifted with dy rather than baseline-shift, which Firefox ignores.
     */
    function richText(text, size) {
        const s = String(text == null ? '' : text);
        const re = /([A-Za-z*]+)_\{([^}]{1,12})\}|([A-Za-z*]+)_([A-Za-z0-9*+]{1,6})|\b([A-Z][A-Za-z]{0,4})(\d{1,2})\b/g;
        let out = '', last = 0, m, pending = false;
        const drop = r2(size * 0.28), small = r2(size * 0.72);
        const plain = (t) => {
            if (!t) return;
            out += pending ? `<tspan dy="${-drop}">${esc(t)}</tspan>` : esc(t);
            pending = false;
        };
        while ((m = re.exec(s))) {
            plain(s.slice(last, m.index));
            const base = m[1] || m[3] || m[5], sub = m[2] || m[4] || m[6];
            plain(base);
            out += `<tspan dy="${drop}" font-size="${small}">${esc(sub)}</tspan>`;
            pending = true;
            last = m.index + m[0].length;
        }
        plain(s.slice(last));
        return out;
    }

    /** A rough rendered width, for keeping labels off each other. Subscript markup doesn't count. */
    const textWidth = (text, size) => String(text || '').replace(/[_{}]/g, '').length * size * 0.56;

    /** Push sorted positions apart until each is at least `gap` from the next, moving both neighbours equally. */
    function spread(items, gap, lo, hi) {
        const sorted = [...items].sort((a, b) => a.pos - b.pos);
        for (let pass = 0; pass < 8; pass++) {
            let moved = false;
            for (let i = 0; i < sorted.length - 1; i++) {
                const d = sorted[i + 1].pos - sorted[i].pos;
                if (d < gap) {
                    const push = (gap - d) / 2;
                    sorted[i].pos -= push; sorted[i + 1].pos += push;
                    moved = true;
                }
            }
            for (const it of sorted) it.pos = Math.min(hi, Math.max(lo, it.pos));
            if (!moved) break;
        }
        return sorted;
    }

    /** Area centroid of a polygon, falling back to the mean of its corners when it has no area. */
    function centroid(vs) {
        let a = 0, cx = 0, cy = 0;
        for (let i = 0; i < vs.length; i++) {
            const p = vs[i], q = vs[(i + 1) % vs.length], cross = p.x * q.y - q.x * p.y;
            a += cross; cx += (p.x + q.x) * cross; cy += (p.y + q.y) * cross;
        }
        if (Math.abs(a) < 1e-6) return [vs.reduce((t, v) => t + v.x, 0) / vs.length, vs.reduce((t, v) => t + v.y, 0) / vs.length];
        return [cx / (3 * a), cy / (3 * a)];
    }

    /** The point a given fraction of the way along a polyline, by length. */
    function along(pts, frac) {
        const lens = [];
        let total = 0;
        for (let i = 0; i < pts.length - 1; i++) { const l = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y); lens.push(l); total += l; }
        let want = total * Math.min(1, Math.max(0, frac));
        for (let i = 0; i < lens.length; i++) {
            if (want <= lens[i] || i === lens.length - 1) {
                const t = lens[i] ? Math.min(1, want / lens[i]) : 0;
                return { x: pts[i].x + t * (pts[i + 1].x - pts[i].x), y: pts[i].y + t * (pts[i + 1].y - pts[i].y) };
            }
            want -= lens[i];
        }
        return pts[pts.length - 1];
    }

    function niceTicks(min, max) {
        const out = [];
        for (let i = 0; i <= 5; i++) {
            const v = min + ((max - min) * i) / 5;
            const r = Math.round(v * 100) / 100;
            out.push({ at: v, label: Number.isInteger(r) ? String(r) : r.toFixed(2).replace(/0+$/, '').replace(/\.$/, '') });
        }
        return out;
    }

    let uid = 0;

    /**
     * @param {object} raw   a spec (normalised again here, so stored rows and
     *                       hand-written specs are equally safe to pass)
     * @param {object} opts  { step, blank, theme, className, width, height,
     *                         background, pixelWidth }
     * @returns {string} an <svg>, or '' when there is nothing to draw
     */
    function render(raw, opts = {}) {
        const spec = normalize(raw);
        if (!spec) return '';
        const theme = { ...DEFAULT_THEME, ...opts.theme, tones: { ...DEFAULT_THEME.tones, ...(opts.theme && opts.theme.tones) } };
        const W = opts.width || 360, H = opts.height || 280;
        const padL = 44, padR = 50, padT = 24, padB = 40;
        const plotW = W - padL - padR, plotH = H - padT - padB;
        const FS = 9, FS_AXIS = 9.5;
        const ax = spec.axes;
        const sx = (x) => r2(padL + ((x - ax.x.min) / (ax.x.max - ax.x.min)) * plotW);
        const sy = (y) => r2(padT + plotH - ((y - ax.y.min) / (ax.y.max - ax.y.min)) * plotH);
        // The axes cross at zero when the range runs either side of it (a maths
        // sketch, a current-voltage graph); otherwise they sit on the left and
        // bottom edges as a first-quadrant diagram expects.
        const axisX = ax.x.min < 0 && ax.x.max > 0 ? sx(0) : padL;
        const axisY = ax.y.min < 0 && ax.y.max > 0 ? sy(0) : padT + plotH;
        const centred = axisX !== padL || axisY !== padT + plotH;
        const inside = (p) => p.x >= ax.x.min - 1e-6 && p.x <= ax.x.max + 1e-6 && p.y >= ax.y.min - 1e-6 && p.y <= ax.y.max + 1e-6;
        const tone = (t) => theme.tones[t] || theme.ink;
        // Where a curve is last seen before it ends or leaves the plot, with the
        // point before it - a curve shifted off the edge is named where it exits.
        const lastVisible = (pts) => {
            for (let i = pts.length - 1; i > 0; i--) {
                const a = pts[i - 1], b = pts[i];
                if (inside(b)) return [a, b];
                // Liang-Barsky: the stretch of a->b that lies inside the plot.
                let t0 = 0, t1 = 1;
                const dx = b.x - a.x, dy = b.y - a.y;
                const edges = [[-dx, a.x - ax.x.min], [dx, ax.x.max - a.x], [-dy, a.y - ax.y.min], [dy, ax.y.max - a.y]];
                let ok = true;
                for (const [p, q] of edges) {
                    if (Math.abs(p) < 1e-12) { if (q < 0) { ok = false; break; } continue; }
                    const t = q / p;
                    if (p < 0) { if (t > t1) { ok = false; break; } if (t > t0) t0 = t; }
                    else { if (t < t0) { ok = false; break; } if (t < t1) t1 = t; }
                }
                if (ok && t1 > t0) return [a, { x: a.x + t1 * dx, y: a.y + t1 * dy }];
            }
            return pts.length && inside(pts[0]) && pts.length === 1 ? [pts[0], pts[0]] : null;
        };
        const maxStep = opts.blank ? 0 : (Number.isInteger(opts.step) && opts.step >= 1 ? opts.step : Infinity);
        const shown = (el) => el.step <= maxStep;
        // The halo keeps a label readable where a curve or guide runs behind it.
        const halo = (o) => (o.halo === false ? '' : ` stroke="${theme.paper}" stroke-width="2.4" stroke-linejoin="round" paint-order="stroke"`);
        const text = (x, y, body, o = {}) => `<text xml:space="preserve"${halo(o)} x="${r2(x)}" y="${r2(y)}" font-family="${esc(theme.font)}" font-size="${o.size || FS}" fill="${o.fill || theme.ink}" text-anchor="${o.anchor || 'start'}"${o.bold ? ' font-weight="700"' : ''}${o.italic ? ' font-style="italic"' : ''}>${richText(body, o.size || FS)}</text>`;

        const { curves, points, coord } = solve(spec);
        const clipId = `cd-clip-${++uid}`, headId = `cd-head-${uid}`;
        const layers = { regions: '', guides: '', curves: '', arrows: '', dots: '', labels: '' };
        const xAxisLabels = [], yAxisLabels = [];

        for (const m of (ax.x.numeric ? niceTicks(ax.x.min, ax.x.max) : [])) xAxisLabels.push({ pos: sx(m.at), text: m.label, tick: true });
        for (const m of (ax.y.numeric ? niceTicks(ax.y.min, ax.y.max) : [])) yAxisLabels.push({ pos: sy(m.at), text: m.label, tick: true });
        if (!opts.blank) {
            for (const m of ax.x.marks) xAxisLabels.push({ pos: sx(m.at), text: m.label });
            for (const m of ax.y.marks) yAxisLabels.push({ pos: sy(m.at), text: m.label });
        }

        for (const r of spec.regions.filter(shown)) {
            const vs = r.vertices.map(coord);
            if (vs.some(v => !v)) continue;
            layers.regions += `<polygon points="${vs.map(v => `${sx(v.x)},${sy(v.y)}`).join(' ')}" fill="${tone(r.tone)}" fill-opacity="0.16" stroke="${tone(r.tone)}" stroke-opacity="0.45" stroke-width="0.6"/>`;
            if (r.label) {
                const [cx, cy] = centroid(vs.map(v => ({ x: sx(v.x), y: sy(v.y) })));
                layers.labels += text(cx, cy + 3, r.label, { anchor: 'middle', size: 7.5, fill: tone(r.tone), bold: true });
            }
        }

        // Curve labels sit just past the curve's last visible point, continuing
        // in the direction it was heading, so a vertical line is named above
        // itself and a rising one off its top-right end.
        const endLabels = [];
        for (const c of spec.curves.filter(shown)) {
            const pts = curves[c.id];
            const d = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.x)},${sy(p.y)}`).join(' ');
            layers.curves += `<path d="${d}" fill="none" stroke="${tone(c.tone)}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"${c.style === 'dashed' ? ' stroke-dasharray="5 3"' : ''}/>`;
            if (!c.label) continue;
            const last = lastVisible(pts);
            if (!last) continue;
            const [a, b] = last;
            let dx = sx(b.x) - sx(a.x), dy = sy(b.y) - sy(a.y);
            const len = Math.hypot(dx, dy) || 1;
            dx /= len; dy /= len;
            endLabels.push({ x: sx(b.x) + dx * 5, y: sy(b.y) + dy * 5, dx, dy, label: c.label, fill: tone(c.tone) });
        }
        // Two curves ending close together (a curve and its shifted twin) would
        // print their names on top of each other.
        const sideLabels = endLabels.filter(l => Math.abs(l.dx) >= 0.35);
        const spreadY = spread(sideLabels.map(l => ({ pos: l.y, l })), FS + 1, padT - 6, padT + plotH + 4);
        for (const it of spreadY) it.l.y = it.pos;
        for (const l of endLabels) {
            let anchor = l.dx > 0.35 ? 'start' : (l.dx < -0.35 ? 'end' : 'middle');
            let lift = Math.abs(l.dx) >= 0.35 ? 3 : (l.dy < 0 ? -2 : 9);
            // A name too long for the margin is tucked back above the end of its curve.
            if (anchor === 'start' && l.x + textWidth(l.label, FS) > W - 2) { anchor = 'end'; l.x = W - 3; lift = -5; }
            layers.labels += text(l.x, l.y + lift, l.label, { anchor, fill: l.fill, bold: true });
        }

        for (const p of spec.points.filter(shown)) {
            const at = points[p.id];
            if (!at || !inside(at)) continue;
            const px = sx(at.x), py = sy(at.y);
            const guide = (x2, y2) => `<line x1="${px}" y1="${py}" x2="${x2}" y2="${y2}" stroke="${theme.guide}" stroke-width="0.9" stroke-dasharray="3 3"/>`;
            if (p.guides === 'both' || p.guides === 'x') layers.guides += guide(px, axisY);
            if (p.guides === 'both' || p.guides === 'y') layers.guides += guide(axisX, py);
            if (p.xLabel) xAxisLabels.push({ pos: px, text: p.xLabel });
            if (p.yLabel) yAxisLabels.push({ pos: py, text: p.yLabel });
            if (p.dot) layers.dots += `<circle cx="${px}" cy="${py}" r="2.4" fill="${theme.ink}"/>`;
            if (p.label) layers.labels += text(px, py - 7, p.label, { bold: true, anchor: 'middle' });
        }

        // Curve-to-curve arrows run horizontally where they can (a curve that
        // moved sideways), vertically where they can't (one that moved up).
        const arrowEnds = (a) => {
            const fromCurve = typeof a.from === 'string' && curves[a.from], toCurve = typeof a.to === 'string' && curves[a.to];
            if (fromCurve && toCurve) {
                const order = a.along === 'y' ? ['x', 'y'] : ['y', 'x'];
                for (const hold of order) {
                    for (const frac of [0.78, 0.6, 0.4, 0.22, 0.9, 0.1]) {
                        const start = along(fromCurve, frac);
                        if (!inside(start)) continue;
                        const hits = crossings(toCurve, hold, start[hold]).filter(inside);
                        if (hits.length) return [start, hits[0]];
                    }
                }
                return null;
            }
            const start = fromCurve ? along(fromCurve, 0.5) : coord(a.from);
            const end = toCurve ? along(toCurve, 0.5) : coord(a.to);
            return start && end ? [start, end] : null;
        };
        let needHead = false;
        for (const a of spec.arrows.filter(shown)) {
            const ends = arrowEnds(a);
            if (!ends) continue;
            let x1 = sx(ends[0].x), y1 = sy(ends[0].y), x2 = sx(ends[1].x), y2 = sy(ends[1].y);
            const len = Math.hypot(x2 - x1, y2 - y1);
            if (len < 6) continue;
            // Stop short of both ends so the head doesn't bury itself in the curve.
            const inset = Math.min(3, len / 5), ux = (x2 - x1) / len, uy = (y2 - y1) / len;
            x1 += ux * inset; y1 += uy * inset; x2 -= ux * inset; y2 -= uy * inset;
            needHead = true;
            layers.arrows += `<line x1="${r2(x1)}" y1="${r2(y1)}" x2="${r2(x2)}" y2="${r2(y2)}" stroke="${theme.ink}" stroke-width="1.1" marker-end="url(#${headId})"${a.heads === 'both' ? ` marker-start="url(#${headId})"` : ''}/>`;
            // Under a level arrow, beside an upright one.
            const level = Math.abs(ux) > Math.abs(uy);
            if (a.label) layers.labels += text((x1 + x2) / 2 + (level ? 0 : 4), (y1 + y2) / 2 + (level ? 10 : 3), a.label, { anchor: level ? 'middle' : 'start', size: 7.5, italic: true });
        }

        for (const n of spec.notes.filter(shown)) {
            const at = coord(n.at);
            if (at) layers.labels += text(sx(at.x), sy(at.y) + 3, n.text, { anchor: 'middle', size: 7.5, italic: true });
        }

        // Axis labels: the y ones are pushed apart vertically; the x ones drop
        // to a second row when two would overlap, since they can't move sideways
        // without ending up under the wrong guide.
        let axisHtml = '';
        for (const it of spread(yAxisLabels.map(l => ({ ...l })), FS, padT, padT + plotH)) {
            axisHtml += text(axisX - 5, it.pos + 3, it.text, { anchor: 'end', fill: it.tick ? theme.muted : theme.ink });
        }
        let lastRight = -Infinity, lastRow = 0;
        for (const it of [...xAxisLabels].sort((a, b) => a.pos - b.pos)) {
            const half = textWidth(it.text, FS) / 2;
            const row = (it.pos - half < lastRight + 2 && lastRow === 0) ? 1 : 0;
            axisHtml += text(it.pos, axisY + 11 + row * 9, it.text, { anchor: 'middle', fill: it.tick ? theme.muted : theme.ink });
            lastRight = it.pos + half; lastRow = row;
        }
        if (ax.origin) axisHtml += text(axisX - 5, axisY + 11, ax.origin, { anchor: 'end' });
        // Titles sit at the far end of each axis, where a hand-drawn diagram
        // puts them, which leaves the length of the axis free for marks.
        // Crossing axes carry a short title (x, y, V, I) at the tip of each
        // axis instead; a long one goes back to the corner.
        const tip = (label) => centred && label.length <= 3;
        if (ax.y.label) {
            axisHtml += tip(ax.y.label)
                ? text(axisX, padT - 9, ax.y.label, { size: FS_AXIS, bold: true, anchor: 'middle' })
                : text(padL - 8, padT - 10, ax.y.label, { size: FS_AXIS, bold: true });
        }
        if (ax.x.label) {
            axisHtml += tip(ax.x.label)
                ? text(padL + plotW + 9, axisY + 3, ax.x.label, { size: FS_AXIS, bold: true })
                : text(padL + plotW + padR - 2, H - 5, ax.x.label, { size: FS_AXIS, bold: true, anchor: 'end' });
        }

        // A backing card, for a diagram that may land on a dark slide: the ink
        // is dark, so without one it would vanish there.
        const backing = opts.background ? `<rect width="${W}" height="${H}" rx="8" fill="${esc(opts.background)}"/>` : '';
        // An intrinsic size, for when the SVG is used as an <img> and has no
        // layout of its own to take one from.
        const size = opts.pixelWidth ? ` width="${opts.pixelWidth}" height="${Math.round(opts.pixelWidth * H / W)}"` : '';
        const cls = opts.className ? ` class="${esc(opts.className)}"` : '';
        const title = spec.title ? `<title>${esc(spec.title)}</title>` : '';
        const defs = `<defs><clipPath id="${clipId}"><rect x="${padL}" y="${padT - 2}" width="${plotW + 2}" height="${plotH + 2}"/></clipPath>${needHead ? `<marker id="${headId}" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="5.5" markerHeight="5.5" orient="auto-start-reverse"><path d="M0,0.8 L8,4 L0,7.2 Z" fill="${theme.ink}"/></marker>` : ''}</defs>`;
        return `<svg${cls}${size} viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"${spec.title ? ` aria-label="${esc(spec.title)}"` : ''}>${title}${defs}${backing}`
            + `<g clip-path="url(#${clipId})">${layers.regions}${layers.guides}${layers.curves}</g>`
            + `<line x1="${axisX}" y1="${padT - 4}" x2="${axisX}" y2="${padT + plotH}" stroke="${theme.ink}" stroke-width="1.3" stroke-linecap="round"/>`
            + `<line x1="${padL}" y1="${axisY}" x2="${padL + plotW + 4}" y2="${axisY}" stroke="${theme.ink}" stroke-width="1.3" stroke-linecap="round"/>`
            + `${layers.arrows}${layers.dots}${axisHtml}${layers.labels}</svg>`;
    }

    /**
     * The diagram as a self-contained image: an SVG data URI and the pixel size
     * it declares. For anywhere that holds a picture by URL rather than markup
     * - a slide's image element - so a drawn diagram can go wherever a found
     * one could. Null when there is nothing to draw.
     */
    function toImage(raw, opts = {}) {
        const pixelWidth = opts.pixelWidth || 1080;
        const svg = render(raw, { background: '#ffffff', ...opts, pixelWidth });
        if (!svg) return null;
        const b64 = typeof Buffer !== 'undefined'
            ? Buffer.from(svg, 'utf8').toString('base64')
            : btoa(unescape(encodeURIComponent(svg)));
        const m = /^<svg[^>]* width="(\d+)" height="(\d+)"/.exec(svg);
        return { url: `data:image/svg+xml;base64,${b64}`, w: Number(m[1]), h: Number(m[2]) };
    }

    /** How many build steps a spec has – 1 for a diagram that appears all at once. */
    function stepCount(raw) {
        const spec = normalize(raw);
        if (!spec) return 0;
        return Math.max(1, ...['curves', 'points', 'regions', 'arrows', 'notes'].flatMap(k => spec[k].map(el => el.step)));
    }

    /**
     * The diagram in words: what is on each axis, which curves there are and
     * what is marked. Alt text for the picture, and what a student who cannot
     * read the diagram is given in its place.
     */
    function describe(raw) {
        const spec = normalize(raw);
        if (!spec) return '';
        const plain = (s) => String(s).replace(/_\{([^}]*)\}/g, '$1').replace(/_/g, '');
        const parts = [];
        if (spec.axes.y.label || spec.axes.x.label) parts.push(`A diagram with ${plain(spec.axes.y.label || 'the vertical axis')} on the vertical axis and ${plain(spec.axes.x.label || 'the horizontal axis')} on the horizontal axis.`);
        const named = spec.curves.filter(c => c.label).map(c => plain(c.label));
        if (named.length) parts.push(`Curves: ${named.join(', ')}.`);
        const marked = spec.points.filter(p => p.label || p.xLabel || p.yLabel)
            .map(p => [p.label, p.yLabel && `at ${p.yLabel}`, p.xLabel && `and ${p.xLabel}`].filter(Boolean).map(plain).join(' '));
        if (marked.length) parts.push(`Marked: ${marked.join('; ')}.`);
        const shaded = spec.regions.filter(r => r.label).map(r => plain(r.label));
        if (shaded.length) parts.push(`Shaded: ${shaded.join(', ')}.`);
        return parts.join(' ');
    }

    return { normalize, solve, unresolved, render, toImage, stepCount, describe, intersections, crossings, LIMITS, TONES, DEFAULT_THEME };
}));
